/**
 * useShareResource Hook
 * Feature 020: Resource Sharing with Care Seeker Clients
 *
 * Mutation hook for sharing resources with care seeker clients via email
 */

import { useCallback, useRef } from 'react';
import {
  useMutation,
  type UseMutateFunction,
  type UseMutateAsyncFunction,
} from '@tanstack/react-query';
import { apiClient } from '@/core/api/client';
import { toast } from 'sonner';

type ShareResourcePayload = {
  resourceId: string;
  resourceTitle: string;
  recipientIds: string[];
};

type ShareResourceRequest = ShareResourcePayload & { idempotencyKey: string };

type ShareResourceResponse = {
  success: boolean;
  sharedCount: number;
};

export function useShareResource() {
  const mutation = useMutation<ShareResourceResponse, Error, ShareResourceRequest>({
    mutationFn: async (payload) => {
      const response = await apiClient.post('/resources/share', payload);
      return response.data;
    },
    onSuccess: (data) => {
      const count = data.sharedCount;
      const message =
        count === 1
          ? 'Resource shared with 1 care seeker'
          : `Resource shared with ${count} care seekers`;

      toast.success(message);
    },
    onError: (error) => {
      console.error('Failed to share resource:', error);
      toast.error('Failed to share resource. Please try again.');
    },
  });

  // The key is minted here rather than in `mutationFn` so it stays fixed for
  // the whole submit: a re-attempt of the same request re-uses the existing
  // shares instead of issuing a second set of tokens and emails. Reused across
  // retries of the *same* payload after a failed attempt (the previous send
  // may have actually gone through server-side) — a new/changed payload still
  // gets a fresh key.
  const lastAttemptRef = useRef<{ payload: string; key: string } | null>(null);
  const resolveKey = useCallback(
    (payload: ShareResourcePayload): string => {
      const serialized = JSON.stringify(payload);
      if (mutation.isError && lastAttemptRef.current?.payload === serialized) {
        return lastAttemptRef.current.key;
      }
      const key = crypto.randomUUID();
      lastAttemptRef.current = { payload: serialized, key };
      return key;
    },
    [mutation.isError],
  );

  const mutate = useCallback<UseMutateFunction<ShareResourceResponse, Error, ShareResourcePayload>>(
    (payload, options) =>
      mutation.mutate({ ...payload, idempotencyKey: resolveKey(payload) }, options),
    [mutation, resolveKey],
  );

  const mutateAsync = useCallback<
    UseMutateAsyncFunction<ShareResourceResponse, Error, ShareResourcePayload>
  >(
    (payload, options) =>
      mutation.mutateAsync({ ...payload, idempotencyKey: resolveKey(payload) }, options),
    [mutation, resolveKey],
  );

  return { ...mutation, mutate, mutateAsync };
}
